import {ExclamationTriangleIcon} from '@heroicons/react/24/solid';
import {captureMessage} from '@sentry/react';
import React from 'react';

import AggOptions from 'src/components/AggOptions';
import {FreezingLine} from 'src/components/FreezingLine';
import RequestFailed from 'src/components/h5/RequestFailed';
import {useAggregation} from 'src/components/h5/useAggregation';
import {makeH5WeatherVizConfig} from 'src/components/h5/weatherVizConfig';
import TimeSeriesViz, {
  STACKED_VIZ_HEIGHT,
  STACKED_VIZ_HEIGHT_WITH_LEGEND,
} from 'src/components/TimeSeriesViz';
import {Translator, useTranslations} from 'src/components/TranslationsContext';
import Viz from 'src/components/Viz';
import WithWidth from 'src/components/WithWidth';
import {useUnitSystem} from 'src/contexts/UnitSystemContext';
import {useH5WeatherData, useH5WeatherListing} from 'src/hooks/h5/useH5Weather';
import {useStableArray} from 'src/hooks/useStableArray';
import {
  H5WeatherDataRequest,
  H5WeatherKind,
  H5WeatherListing,
  H5_MAX_VALID_TIME_WINDOW_DAYS,
  H5_WEATHER_KINDS,
  UnsupportedH5SchemaError,
  isRetryableH5Error,
  weatherListingsForSection,
} from 'src/lib/api/h5';
import {Project, Site} from 'src/lib/api/project';
import {DashboardSection, ForecastTime, TimeAggregation, WeatherColumns} from 'src/lib/api/series';
import {formatDateShortFull} from 'src/lib/dates';
import {nearestForecastTimes, weatherForecastTimesWithoutData} from 'src/lib/h5/forecastTimes';
import {
  climatologyChartSourceFor,
  toWeatherClimatologyTimeSeries,
} from 'src/lib/h5/toWeatherClimatologyTimeSeries';
import {chartSourceFor, toWeatherForecastTimeSeries} from 'src/lib/h5/toWeatherForecastTimeSeries';
import {toWeatherObservationTimeSeries} from 'src/lib/h5/toWeatherObservationTimeSeries';
import {ConfigBySourceId} from 'src/lib/sourceConfigs';

type AvailableWeatherListing = Extract<H5WeatherListing, {status: 'available'}>;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const UNAVAILABLE_NOTICE_BY_KIND = {
  forecast: 'weatherSourceUnavailable',
  observation: 'weatherObservationsUnavailable',
  climatology: 'weatherClimatologyUnavailable',
} as const satisfies Record<H5WeatherKind, Parameters<Translator>[0]>;

const sourceName = (resource: H5WeatherListing) => {
  const source = chartSourceFor(resource.source);
  return (source && ConfigBySourceId[source]?.display) ?? resource.source;
};

/**
 * The weather configured for an H5 section, drawn under its flow chart: one precipitation panel
 * and one temperature panel of forecasts, with any observed weather and climatology behind them,
 * and the legacy weather chart's own aggregation controls. The section header's run selection
 * drives the weather forecasts too, paired the way the legacy chart pairs them (see
 * nearestForecastTimes), since flow and weather are initialized on different schedules.
 */
export default function WeatherCharts({
  project,
  site,
  section,
  forecastTimes,
  selectedForecastTimes,
  dateRange,
  setDateRange,
  initTimeRange,
  defaultTimeAggregation,
  analyticsSection,
}: {
  project: Project;
  site: Site;
  section: DashboardSection;
  // The flow runs on offer, newest first, and the ones selected.
  forecastTimes: ForecastTime[] | undefined;
  selectedForecastTimes: ForecastTime[];
  dateRange: [Date, Date];
  setDateRange: (dateRange: [Date, Date]) => void;
  initTimeRange: [Date, Date];
  defaultTimeAggregation: TimeAggregation;
  analyticsSection: string;
}) {
  const {t} = useTranslations();
  const unitSystem = useUnitSystem();
  const weatherAnalyticsSection = `${analyticsSection}_weather`;
  const listingQuery = useH5WeatherListing(site, project.id, initTimeRange);
  const resources = React.useMemo(
    () => weatherListingsForSection(listingQuery.data, section),
    [listingQuery.data, section]
  );
  // Weather forecasts are paired with flow runs, so until the flow side has runs on offer they are
  // neither drawn nor reported on. Observed weather and climatologies don't wait for them.
  const flowHasRuns = !!forecastTimes?.length;
  const availableResources = React.useMemo(
    () =>
      flowHasRuns
        ? resources.filter(
            (resource): resource is AvailableWeatherListing => resource.status === 'available'
          )
        : [],
    [resources, flowHasRuns]
  );

  const pairings = availableResources.map((resource) => ({
    resource,
    runs: nearestForecastTimes(
      selectedForecastTimes,
      resource.availableForecasts,
      forecastTimes?.[0]
    ),
  }));
  const queried = pairings.filter(({runs}) => runs.length > 0);

  const {aggregation, updateAggregation} = useAggregation(dateRange, {
    timeAggregation: defaultTimeAggregation,
    rateVolumeMode: 'rate',
  });

  // One read per paired resource; dataQueries line up with `queried`.
  const dataQueries = useH5WeatherData(
    site,
    queried.map(({resource, runs}) => ({
      projectId: project.id,
      weatherId: resource.weatherId,
      // Sorted so one set of runs is one query key however it was picked.
      initTimes: runs.map((run) => run.initializationTime.toISOString()).sort(),
      validTimeStartDate: dateRange[0].toISOString(),
      validTimeEndDate: dateRange[1].toISOString(),
      unitSystem,
      timeAggregation: aggregation.timeAggregation,
      aggregationMethod: aggregation.aggregationMethod,
      // Never 'level': the controls do not offer it here.
      rateVolumeMode: aggregation.rateVolumeMode as H5WeatherDataRequest['rateVolumeMode'],
      timeZone: site.localTimeZone,
    }))
  );

  // Observations and climatologies are read by valid time over the chart's window, which the route
  // caps; past the cap they are left out.
  const readsValidTimes =
    (dateRange[1].getTime() - dateRange[0].getTime()) / MS_PER_DAY <= H5_MAX_VALID_TIME_WINDOW_DAYS;
  const observationResources = React.useMemo(
    () =>
      readsValidTimes
        ? weatherListingsForSection(listingQuery.data, section, 'observation').filter(
            (resource): resource is AvailableWeatherListing =>
              resource.status === 'available' && !!chartSourceFor(resource.source)
          )
        : [],
    [listingQuery.data, section, readsValidTimes]
  );
  const climatologyResources = React.useMemo(
    () =>
      readsValidTimes
        ? weatherListingsForSection(listingQuery.data, section, 'climatology').filter(
            (resource): resource is AvailableWeatherListing =>
              resource.status === 'available' && !!climatologyChartSourceFor(resource.source)
          )
        : [],
    [listingQuery.data, section, readsValidTimes]
  );
  const validTimeRequest = {
    projectId: project.id,
    validTimeStartDate: dateRange[0].toISOString(),
    validTimeEndDate: dateRange[1].toISOString(),
    unitSystem,
    timeZone: site.localTimeZone,
  };
  const observationQueries = useH5WeatherData(
    site,
    observationResources.map((resource) => ({
      ...validTimeRequest,
      weatherId: resource.weatherId,
      timeAggregation: aggregation.timeAggregation,
      aggregationMethod: aggregation.aggregationMethod,
      rateVolumeMode: aggregation.rateVolumeMode as H5WeatherDataRequest['rateVolumeMode'],
    }))
  );
  // Daily mean rates whatever the chart's aggregation, binned to the site's local days: the route
  // serves a climatology no other way.
  const climatologyQueries = useH5WeatherData(
    site,
    climatologyResources.map((resource) => ({
      ...validTimeRequest,
      weatherId: resource.weatherId,
      timeAggregation: '1D',
      aggregationMethod: 'mean',
      rateVolumeMode: 'rate',
    }))
  );
  const observationResponses = useStableArray(observationQueries.map((query) => query.data));
  const climatologyResponses = useStableArray(climatologyQueries.map((query) => query.data));
  const observationTimeSeriesList = React.useMemo(
    () => [
      ...observationResponses.flatMap((observationData) =>
        observationData?.kind === 'observation'
          ? (toWeatherObservationTimeSeries(observationData, {siteId: site.id}) ?? [])
          : []
      ),
      ...climatologyResponses.flatMap((climatologyData) =>
        climatologyData?.kind === 'climatology'
          ? (toWeatherClimatologyTimeSeries(climatologyData, {siteId: site.id}) ?? [])
          : []
      ),
    ],
    [observationResponses, climatologyResponses, site.id]
  );
  const allDataQueries = [...dataQueries, ...observationQueries, ...climatologyQueries];
  // The chart waits for its forecasts only. Observed weather and climatologies read the whole
  // window, which takes much longer, so they join the drawn forecasts when they arrive.
  const awaitedQueries = dataQueries.length > 0 ? dataQueries : allDataQueries;

  const unsupportedSchema =
    listingQuery.error instanceof UnsupportedH5SchemaError ||
    allDataQueries.some((query) => query.error instanceof UnsupportedH5SchemaError);
  const showsChart =
    availableResources.length + observationResources.length + climatologyResources.length > 0 &&
    !unsupportedSchema;
  const vizConfig = React.useMemo(
    () =>
      makeH5WeatherVizConfig({
        sources: availableResources.flatMap((resource) => chartSourceFor(resource.source) ?? []),
        observations: observationResources.flatMap((resource) => {
          const source = chartSourceFor(resource.source);
          return source ? [{source, name: sourceName(resource)}] : [];
        }),
        climatologies: climatologyResources.flatMap((resource) => {
          const source = climatologyChartSourceFor(resource.source);
          return source ? [{source, name: sourceName(resource)}] : [];
        }),
        timeAggregation: aggregation.timeAggregation,
        rateVolumeMode: aggregation.rateVolumeMode,
        t,
      }),
    [
      availableResources,
      observationResources,
      climatologyResources,
      aggregation.timeAggregation,
      aggregation.rateVolumeMode,
      t,
    ]
  );
  const weatherData = useStableArray(dataQueries.map((query) => query.data));
  const forecastTimeSeriesList = React.useMemo(
    () =>
      weatherData.flatMap((data) =>
        data?.kind === 'forecast'
          ? (toWeatherForecastTimeSeries(data, {siteId: site.id}, (message) =>
              captureMessage(message)
            ) ?? [])
          : []
      ),
    [weatherData, site.id]
  );
  const timeZone = weatherData.find((data) => data)?.timeZone ?? site.localTimeZone;

  const notices = ((): React.ReactNode[] => {
    if (unsupportedSchema) return [t('unsupportedForecastData')];
    if (listingQuery.isError) {
      return [
        <RequestFailed
          key="listing"
          message={t('weatherRequestFailed')}
          error={listingQuery.error}
          onRetry={listingQuery.refetch}
        />,
      ];
    }
    // A source that failed to list is named, with the route's reason, beside the others' chart.
    // Each kind is reported on only when its resources would be drawn.
    const reportedKinds = H5_WEATHER_KINDS.filter((kind) =>
      kind === 'forecast' ? flowHasRuns : readsValidTimes
    );
    const notices: React.ReactNode[] = reportedKinds
      .flatMap((kind) => weatherListingsForSection(listingQuery.data, section, kind))
      .flatMap((resource) =>
        resource.status === 'unavailable'
          ? [
              t(UNAVAILABLE_NOTICE_BY_KIND[resource.kind], {
                source: sourceName(resource),
                reason: resource.reason,
              }),
            ]
          : []
      );
    // One note covers every source whose read failed; its retry refetches them all, offered
    // when any of the failures is one a retry can clear.
    const failedQueries = allDataQueries.filter((query) => query.isError);
    if (failedQueries.length > 0) {
      const retryable = failedQueries.find((query) => isRetryableH5Error(query.error));
      notices.push(
        <RequestFailed
          key="data"
          message={t('weatherRequestFailed')}
          error={(retryable ?? failedQueries[0]).error}
          onRetry={() => failedQueries.forEach((query) => query.refetch())}
        />
      );
    }
    if (availableResources.length === 0) return notices;
    if (availableResources.every((resource) => resource.availableForecasts.length === 0)) {
      return [...notices, t('noForecastsInPeriod')];
    }
    if (selectedForecastTimes.length === 0) return [...notices, t('noForecastsSelected')];
    const unpaired = pairings.filter(
      ({resource, runs}) => resource.availableForecasts.length > 0 && runs.length === 0
    );
    if (unpaired.length > 0) {
      notices.push(
        t('noWeatherForecastsNearSelection', {
          sources: unpaired.map(({resource}) => sourceName(resource)).join(', '),
        })
      );
    }
    const withoutData = new Set(
      queried.flatMap(({runs}, index) => {
        const data = dataQueries[index].data;
        return data?.kind === 'forecast'
          ? weatherForecastTimesWithoutData(runs, data).map((run) =>
              formatDateShortFull(run.initializationTime, timeZone)
            )
          : [];
      })
    );
    if (withoutData.size > 0) {
      notices.push(t('forecastTimesWithoutData', {forecasts: Array.from(withoutData).join(', ')}));
    }
    return notices;
  })();

  // Inside the chart area the notes read as the weather chart's own, not the flow chart's.
  const notice =
    notices.length > 0 ? (
      <div className="flex flex-col items-center gap-2 px-4 pt-2">
        {notices.map((message, index) => (
          <p
            // Notes have no identity of their own; the list is rebuilt whole each render.
            key={index}
            className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm text-amber-900"
          >
            <ExclamationTriangleIcon
              className="h-4 w-4 shrink-0 text-amber-500"
              aria-hidden="true"
            />
            {message}
          </p>
        ))}
      </div>
    ) : null;
  if (!showsChart) return notice;
  return (
    <WithWidth>
      {(width) => (
        <Viz
          section={t(section)}
          title={t('weather')}
          site={site}
          config={vizConfig}
          renderOptions={() => (
            <AggOptions
              site={site}
              agg={aggregation}
              updateAgg={updateAggregation}
              dateRange={dateRange}
              showLevelOption={false}
              analyticsSection={weatherAnalyticsSection}
            />
          )}
          forecastQueries={[]}
          observationQueries={[]}
          forecastTimeSeriesList={forecastTimeSeriesList}
          observationTimeSeriesList={observationTimeSeriesList}
          loadingStateDims={[width, STACKED_VIZ_HEIGHT_WITH_LEGEND * 2]}
          dateRange={dateRange}
          setDateRange={setDateRange}
          isLoading={awaitedQueries.some((query) => query.isLoading)}
        >
          {(props) => (
            <>
              {notice}
              <TimeSeriesViz
                {...props}
                timeZone={timeZone}
                groupId={t('precipitation')}
                dims={[width, STACKED_VIZ_HEIGHT]}
                range={[0, 'auto']}
                minMaxRangeConstraints={[null, 0.1]}
              />
              <TimeSeriesViz
                {...props}
                timeZone={timeZone}
                groupId={t('temperature')}
                dims={[width, STACKED_VIZ_HEIGHT]}
                renderSvg={({y, graphDims, forecastTimeSeriesList: series}) => {
                  const temperature = series.find(
                    (ts) => !!ts.variables[WeatherColumns.TEMPERATURE]
                  );
                  return temperature ? (
                    <FreezingLine y={y} graphDims={graphDims} temperatureTimeSeries={temperature} />
                  ) : null;
                }}
              />
            </>
          )}
        </Viz>
      )}
    </WithWidth>
  );
}
